import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * Quantity Selector
 *
 * Normal:
 *   1 → 2 → 3 → 4
 *
 * Fabric:
 *   Customer sees:
 *   3 → 3.5 → 4 → 4.5 → 5
 *
 *   Shopify stores:
 *   6 → 7 → 8 → 9 → 10
 *
 * Fabric stock:
 *   Shopify inventory 15
 *   Customer-visible maximum = 7.5 meters
 */

export class QuantitySelectorComponent extends Component {
  requiredRefs = [
    'quantityInput',
    'minusButton',
    'plusButton'
  ];

  serverDisabledMinus = false;
  serverDisabledPlus = false;
  initialized = false;

  connectedCallback() {
    super.connectedCallback();

    const {
      minusButton,
      plusButton
    } = this.refs;

    if (minusButton.disabled) {
      this.serverDisabledMinus = true;
    }

    if (plusButton.disabled) {
      this.serverDisabledPlus = true;
    }

    this.initialized = true;

    this.updateButtonStates();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  /**
   * Parse decimal-safe number.
   */
  parseNumber(value, fallback = 0) {
    const number = Number.parseFloat(value);

    return Number.isFinite(number)
      ? number
      : fallback;
  }

  /**
   * Fix JavaScript floating point issues.
   */
  roundQuantity(value) {
    return Number(
      Number(value).toFixed(4)
    );
  }

  /**
   * Is this Fabric?
   */
  isFabric() {
    return (
      this.refs.quantityInput.dataset.fabric === 'true'
    );
  }

  /**
   * Is this selector inside cart?
   */
  isCartMode() {
    return (
      this.refs.quantityInput.dataset.cartMode === 'true'
    );
  }

  /**
   * Update cart quantity.
   */
  setCartQuantity(cartQty) {
    this.refs.quantityInput.setAttribute(
      'data-cart-quantity',
      this.parseNumber(
        cartQty,
        0
      ).toString()
    );

    this.updateCartQuantity();
  }

  /**
   * Can add to cart?
   */
  canAddToCart() {
    const {
      max,
      cartQuantity,
      value
    } = this.getCurrentValues();

    const quantityToAdd = value;

    const wouldExceedMax =
      max !== null &&
      cartQuantity +
        quantityToAdd >
        max;

    return {
      canAdd:
        !wouldExceedMax,

      maxQuantity:
        max,

      cartQuantity:
        cartQuantity,

      quantityToAdd:
        quantityToAdd
    };
  }

  /**
   * Get current input value.
   */
  getValue() {
    return this.refs.quantityInput.value;
  }

  /**
   * Set visible value.
   */
  setValue(value) {
    const quantity =
      this.roundQuantity(
        this.parseNumber(
          value,
          0
        )
      );

    this.refs.quantityInput.value =
      quantity.toString();

    this.syncFabricShopifyQuantity();

    this.updateButtonStates();
  }

  /**
   * Update constraints.
   */
  updateConstraints(
    min,
    max,
    step
  ) {
    const {
      quantityInput
    } = this.refs;

    const currentValue =
      this.parseNumber(
        quantityInput.value,
        0
      );

    quantityInput.min = min;

    if (
      max !== null &&
      max !== undefined &&
      max !== ''
    ) {
      quantityInput.max = max;
    } else {
      quantityInput.removeAttribute('max');
    }

    quantityInput.step = step;

    const newMin =
      this.parseNumber(
        min,
        1
      );

    const newStep =
      this.parseNumber(
        step,
        1
      );

    const effectiveMax =
      this.getEffectiveMax();

    let newValue =
      currentValue;

    if (newStep > 0) {
      const stepsFromMin =
        (currentValue -
          newMin) /
        newStep;

      const aligned =
        Math.abs(
          stepsFromMin -
            Math.round(
              stepsFromMin
            )
        ) < 0.0001;

      if (!aligned) {
        newValue =
          newMin +
          Math.floor(
            stepsFromMin
          ) *
          newStep;
      }
    }

    newValue =
      Math.max(
        newMin,
        Math.min(
          effectiveMax ??
            Infinity,
          newValue
        )
      );

    newValue =
      this.roundQuantity(
        newValue
      );

    quantityInput.value =
      newValue.toString();

    this.syncFabricShopifyQuantity();

    this.updateButtonStates();
  }

  /**
   * Read current values.
   */
  getCurrentValues() {
    const {
      quantityInput
    } = this.refs;

    const min =
      this.parseNumber(
        quantityInput.min,
        1
      );

    const max =
      quantityInput.max !== ''
        ? this.parseNumber(
            quantityInput.max,
            null
          )
        : null;

    const step =
      this.parseNumber(
        quantityInput.step,
        1
      );

    const value =
      this.parseNumber(
        quantityInput.value,
        min
      );

    const cartQuantity =
      this.parseNumber(
        quantityInput.getAttribute(
          'data-cart-quantity'
        ),
        0
      );

    return {
      min,
      max,
      step,
      value,
      cartQuantity
    };
  }

  /**
   * Convert visible Fabric quantity
   * into Shopify quantity.
   *
   * Example:
   *
   * 3 m   = 6 Shopify units
   * 3.5 m = 7 Shopify units
   * 4 m   = 8 Shopify units
   * 4.5 m = 9 Shopify units
   */
  syncFabricShopifyQuantity() {
    if (!this.isFabric()) {
      return;
    }

    const {
      quantityInput
    } = this.refs;

    const fabricShopifyQuantity =
      this.refs.fabricShopifyQuantity;

    if (!fabricShopifyQuantity) {
      return;
    }

    let visibleQuantity =
      this.parseNumber(
        quantityInput.value,
        3
      );

    visibleQuantity =
      this.roundQuantity(
        visibleQuantity
      );

    const shopifyQuantity =
      Math.round(
        visibleQuantity * 2
      );

    fabricShopifyQuantity.value =
      shopifyQuantity.toString();
  }

  /**
   * Calculate effective max.
   *
   * Fabric:
   *
   * Inventory = 15 Shopify units
   * Maximum customer quantity = 7.5 meters
   *
   * Product page:
   * Existing cart quantity is deducted.
   *
   * Cart drawer:
   * Existing cart quantity is NOT deducted
   * because we are editing that existing line.
   */
  getEffectiveMax() {
    const {
      max,
      cartQuantity,
      min
    } = this.getCurrentValues();

    /*
     * ==========================================
     * FABRIC
     * ==========================================
     */
    if (this.isFabric()) {
      if (max === null) {
        return null;
      }

      /*
       * CART DRAWER
       *
       * The current line already represents
       * the quantity in the cart.
       *
       * Therefore do not subtract cartQuantity.
       *
       * Example:
       *
       * Stock = 15
       * Current cart = 7
       * Visible = 3.5
       *
       * Maximum remains 7.5 meters.
       */
      if (this.isCartMode()) {
        return Math.max(
          max,
          min
        );
      }

      /*
       * PRODUCT PAGE
       *
       * Existing cart quantity must be
       * deducted from available stock.
       *
       * Example:
       *
       * Stock = 15
       * Cart = 2 Shopify units
       *
       * Remaining:
       * 13 Shopify units
       *
       * Visible:
       * 6.5 meters
       */
      const cartCustomerQuantity =
        cartQuantity / 2;

      return Math.max(
        max -
          cartCustomerQuantity,
        min
      );
    }

    /*
     * ==========================================
     * NORMAL PRODUCT
     * ==========================================
     */
    if (max !== null) {
      return Math.max(
        max -
          cartQuantity,
        min
      );
    }

    /*
     * ==========================================
     * NORMAL PRODUCT INVENTORY
     * ==========================================
     */
    const inventory =
      this.parseNumber(
        this.dataset.inventoryQuantity,
        null
      );

    const inventoryPolicy =
      this.dataset.inventoryPolicy;

    if (
      inventory !== null &&
      inventoryPolicy === 'deny'
    ) {
      return Math.max(
        inventory -
          cartQuantity,
        min
      );
    }

    return null;
  }

  /**
   * Update +/- button states.
   */
  updateButtonStates() {
    const {
      minusButton,
      plusButton
    } = this.refs;

    const {
      min,
      value
    } = this.getCurrentValues();

    const effectiveMax =
      this.getEffectiveMax();

    /*
     * MINUS
     */
    if (!this.serverDisabledMinus) {
      minusButton.disabled =
        value <=
        min + 0.0001;
    }

    /*
     * PLUS
     */
    if (!this.serverDisabledPlus) {
      plusButton.disabled =
        effectiveMax !== null &&
        value >=
          effectiveMax -
            0.0001;
    }

    /*
     * Update native max.
     */
    if (
      effectiveMax !== null
    ) {
      this.refs.quantityInput.max =
        effectiveMax.toString();
    }
  }

  /**
   * Update quantity.
   */
  updateQuantity(
    stepMultiplier
  ) {
    const {
      quantityInput
    } = this.refs;

    const {
      min,
      step,
      value
    } = this.getCurrentValues();

    const effectiveMax =
      this.getEffectiveMax();

    let newValue =
      value +
      step *
        stepMultiplier;

    /*
     * Respect minimum.
     */
    newValue =
      Math.max(
        min,
        newValue
      );

    /*
     * Respect maximum.
     */
    if (
      effectiveMax !== null
    ) {
      newValue =
        Math.min(
          effectiveMax,
          newValue
        );
    }

    /*
     * Fix decimals.
     */
    newValue =
      this.roundQuantity(
        newValue
      );

    quantityInput.value =
      newValue.toString();

    /*
     * Keep hidden Shopify quantity
     * synchronized.
     */
    this.syncFabricShopifyQuantity();

    /*
     * Notify parent.
     */
    this.onQuantityChange();

    this.updateButtonStates();
  }

  /**
   * Increase.
   */
  increaseQuantity(event) {
    if (
      !(event.target instanceof
        HTMLElement)
    ) {
      return;
    }

    event.preventDefault();

    this.updateQuantity(1);
  }

  /**
   * Decrease.
   */
  decreaseQuantity(event) {
    if (
      !(event.target instanceof
        HTMLElement)
    ) {
      return;
    }

    event.preventDefault();

    this.updateQuantity(-1);
  }

  /**
   * Select input on focus.
   */
  selectInputValue(event) {
    const {
      quantityInput
    } = this.refs;

    if (
      !(
        event.target instanceof
        HTMLInputElement
      ) ||
      document.activeElement !==
        quantityInput
    ) {
      return;
    }

    quantityInput.select();
  }

  /**
   * Set quantity manually.
   */
  setQuantity(event) {
    if (
      !(
        event.target instanceof
        HTMLInputElement
      )
    ) {
      return;
    }

    event.preventDefault();

    const {
      quantityInput
    } = this.refs;

    const {
      min,
      step
    } = this.getCurrentValues();

    const effectiveMax =
      this.getEffectiveMax();

    let quantity =
      this.parseNumber(
        event.target.value,
        min
      );

    /*
     * Minimum.
     */
    quantity =
      Math.max(
        min,
        quantity
      );

    /*
     * Maximum.
     */
    quantity =
      Math.min(
        effectiveMax ??
          Infinity,
        quantity
      );

    /*
     * Align to step.
     *
     * Fabric:
     *
     * 3
     * 3.5
     * 4
     * 4.5
     * 5
     */
    if (step > 0) {
      const steps =
        Math.round(
          (quantity -
            min) /
          step
        );

      quantity =
        min +
        steps *
          step;
    }

    /*
     * Fix decimals.
     */
    quantity =
      this.roundQuantity(
        quantity
      );

    /*
     * Final max check.
     */
    if (
      effectiveMax !== null
    ) {
      quantity =
        Math.min(
          quantity,
          effectiveMax
        );
    }

    quantityInput.value =
      quantity.toString();

    /*
     * Update hidden Shopify quantity.
     *
     * 3 m   → 6
     * 3.5 m → 7
     * 4 m   → 8
     * 4.5 m → 9
     */
    this.syncFabricShopifyQuantity();

    /*
     * Notify parent.
     */
    this.onQuantityChange();

    this.updateButtonStates();
  }

  /**
   * Notify parent components.
   *
   * Normal products:
   *   visible quantity is Shopify quantity.
   *
   * Fabric:
   *   visible quantity is meters.
   *   Shopify quantity is visible × 2.
   */
  onQuantityChange() {
    const {
      quantityInput
    } = this.refs;

    let newValue =
      this.parseNumber(
        quantityInput.value,
        0
      );

    /*
     * Fabric quantity conversion.
     *
     * Customer:
     *   3.5 meters
     *
     * Shopify:
     *   7 units
     */
    if (this.isFabric()) {
      newValue =
        Math.round(
          newValue * 2
        );

      const fabricShopifyQuantity =
        this.refs.fabricShopifyQuantity;

      if (fabricShopifyQuantity) {
        fabricShopifyQuantity.value =
          newValue.toString();
      }
    }

    this.dispatchEvent(
      new QuantitySelectorUpdateEvent(
        newValue,
        Number(
          quantityInput.dataset
            .cartLine
        ) || undefined
      )
    );
  }

  /**
   * Update cart quantity.
   */
  updateCartQuantity() {
    const {
      quantityInput
    } = this.refs;

    const {
      min,
      value
    } = this.getCurrentValues();

    const effectiveMax =
      this.getEffectiveMax();

    let clampedValue =
      Math.max(
        min,
        value
      );

    if (
      effectiveMax !== null
    ) {
      clampedValue =
        Math.min(
          effectiveMax,
          clampedValue
        );
    }

    clampedValue =
      this.roundQuantity(
        clampedValue
      );

    quantityInput.value =
      clampedValue.toString();

    /*
     * Keep Shopify quantity synchronized.
     */
    this.syncFabricShopifyQuantity();

    this.updateButtonStates();
  }

  /**
   * Quantity input getter.
   */
  get quantityInput() {
    if (
      !this.refs.quantityInput
    ) {
      throw new Error(
        'Missing <input ref="quantityInput" /> inside <quantity-selector-component />'
      );
    }

    return this.refs.quantityInput;
  }
}

if (
  !customElements.get(
    'quantity-selector-component'
  )
) {
  customElements.define(
    'quantity-selector-component',
    QuantitySelectorComponent
  );
}